import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBrand } from "../../contexts/BrandContext";
import { http, formatErr } from "../../lib/api";
import { PasswordField } from "../../components/PasswordField";
import { BrandLogoCircle } from "../../components/BrandLogoCircle";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";

export default function AgentRegister() {
  const { brand } = useBrand();
  const nav = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 12) return toast.error("Password minima 12 caratteri.");
    setBusy(true);
    try {
      await http.post("/crm/agents/register", f);
      setDone(true);
      toast.success("Registrazione ricevuta. Per proseguire serve la verifica dell'email.");
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  return (
    <div data-testid="agent-register-page" className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, var(--brand-dark) 0%, #2d1526 100%)", color: "white" }}>
      <form onSubmit={submit} className="w-full max-w-md p-8 rounded-2xl border border-white/10 backdrop-blur-sm"
        style={{ background: "rgba(255,255,255,0.04)" }}>
        <div className="flex flex-col items-center text-center mb-6">
          <BrandLogoCircle className="mb-4 h-28 w-28" imgTestId="agent-register-logo" />
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="w-4 h-4" style={{ color: "var(--brand-accent)" }} />
            <span className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "var(--brand-accent)" }}>Diventa agente</span>
          </div>
          <h1 className="font-serif text-2xl">Entra nel team {brand.display_name}</h1>
        </div>

        {done ? (
          <div data-testid="agent-register-done" className="text-center text-sm text-white/80 space-y-4">
            <p>Registrazione ricevuta per <b>{f.email}</b>. Per accedere alla candidatura devi confermare l'email tramite il link di verifica.
              Se il messaggio non arriva, accedi e usa "Reinvia email di verifica" dalla tua dashboard: ti diremo se l'invio è riuscito.</p>
            <Link to="/platform/login" className="inline-block px-4 py-2 rounded-lg text-sm"
              style={{ background: "var(--brand-primary)" }} data-testid="agent-register-to-login">Vai al login</Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Nome" testid="ar-first" value={f.first_name} onChange={on("first_name")} />
              <Field label="Cognome" testid="ar-last" value={f.last_name} onChange={on("last_name")} />
            </div>
            <label className="block mb-4">
              <span className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Email</span>
              <input type="email" required value={f.email} onChange={on("email")} data-testid="ar-email"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none" />
            </label>
            <label className="block mb-6">
              <span className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Password (min 12)</span>
              <PasswordField required value={f.password} onChange={on("password")} data-testid="ar-password"
                iconClassName="text-white/70"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none" />
            </label>
            <button type="submit" disabled={busy} data-testid="agent-register-submit"
              className="w-full py-3 rounded-lg disabled:opacity-50 uppercase tracking-widest text-sm font-medium"
              style={{ background: "var(--brand-primary)", color: "white" }}>
              {busy ? <Loader2 className="inline w-4 h-4 mr-2 animate-spin" /> : null} Registrati
            </button>
            <div className="mt-5 text-center">
              <Link to="/platform/login" className="text-xs text-white/70 hover:text-white underline" data-testid="ar-login-link">
                Hai già un account? Accedi
              </Link>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

function Field({ label, testid, value, onChange }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60 mb-2 block">{label}</span>
      <input required value={value} onChange={onChange} data-testid={testid}
        className="w-full px-3 py-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none" />
    </label>
  );
}
